import { getStrain } from '../data/strains';

// Recalculate quality from current terpene/visual/THC (same formula as harvest)
function recalcQuality(slot) {
  const strain = getStrain(slot.strainId);
  const thcScore = Math.min(100, (slot.thcAccumulation / (strain?.thcCeiling ?? 25)) * 100);
  return Math.round(thcScore * 0.40 + slot.terpeneProfile * 0.35 + slot.visualAppeal * 0.25);
}

// Advance environment drift toward recipe targets (called once per game-day)
function advanceCuringEnvironment(room) {
  const recipe = room.recipe ?? { temperature: 18, humidity: 62 };
  const hvacEff = room.equipment?.hvac?.efficiency ?? 0.70;
  const hasHumidifier = room.equipment?.humidifier?.installed ?? false;

  // Lower efficiency → wider drift band
  const tempRange = (1 - hvacEff) * 10; // 0.70 eff → ±3°C
  const humRange = hasHumidifier ? 2 : 8;  // ±2% or ±8%

  const newTemp = recipe.temperature + (Math.random() * 2 - 1) * tempRange;
  const newHum  = recipe.humidity    + (Math.random() * 2 - 1) * humRange;

  return {
    ...room,
    environment: { temperature: newTemp, humidity: newHum },
  };
}

// Condition score based on deviation of actuals from recipe targets
function getConditionScore(room) {
  const env    = room.environment ?? {};
  const recipe = room.recipe ?? { temperature: 18, humidity: 62 };
  const rTemp = recipe.temperature || 18;
  const rHum  = recipe.humidity    || 62;
  const tempDev = Math.abs((env.temperature ?? rTemp) - rTemp) / rTemp;
  const humDev  = Math.abs((env.humidity    ?? rHum)  - rHum)  / rHum;
  if (tempDev < 0.05 && humDev < 0.05) return 1.0;
  if (tempDev < 0.15 && humDev < 0.15) return 0.5;
  return 0.0;
}

// Worker modifier: sum 0.15 × skill/100 per trimmer; baseline 1.0 if none assigned
function getWorkerMod(room, workers) {
  const trimmers = (room.assignedWorkers ?? [])
    .map(id => workers.find(w => w.id === id))
    .filter(w => w?.type === 'trimmer');
  return 1.0 + trimmers.reduce((sum, w) => sum + 0.15 * (w.skill / 100), 0);
}

// Pure function: advance all curingSlots by 1 day
export function processCuringDay(state) {
  const curingRoom = state.rooms.find(r => r.type === 'curing' && r.unlocked);
  if (!curingRoom || !curingRoom.curingSlots?.length) return state;

  const updatedRoom    = advanceCuringEnvironment(curingRoom);
  const conditionScore = getConditionScore(updatedRoom);
  const workerMod      = getWorkerMod(updatedRoom, state.workers);

  let newNotifications = state.notifications;
  const newSlots = [];

  for (const slot of updatedRoom.curingSlots) {
    const newCuringDay = slot.curingDay + 1;

    // Quality gains during days 1-10
    let newTerpene = slot.terpeneProfile;
    let newVisual  = slot.visualAppeal;
    if (newCuringDay <= 10) {
      newTerpene = Math.min(100, slot.terpeneProfile + (0.8 + conditionScore * 0.7) * workerMod);
      newVisual  = Math.min(100, slot.visualAppeal  + (0.4 + conditionScore * 0.5) * workerMod);
    }

    // Record peak day on curingDay 10
    let newPeakDay = slot.peakDay;
    if (newCuringDay === 10 && newPeakDay === null) {
      newPeakDay = state.gameDay;
    }

    // Degradation after curingDay 14
    let newMoldRisk = slot.moldRisk;
    let newStatus   = slot.status;
    if (newCuringDay > 14) {
      newStatus   = 'degrading';
      newMoldRisk = slot.moldRisk + 2.5; // unconditional — 40 days to destruction
      newTerpene  = Math.max(0, slot.terpeneProfile - 0.5);
    }

    // Mold destruction at moldRisk >= 100
    if (newMoldRisk >= 100) {
      newNotifications = [
        ...newNotifications,
        {
          id: `notif-mold-${slot.inventoryId}-day${state.gameDay}`,
          message: `🧫 Curing batch lost to mold! (${slot.strainId.replace(/-/g, ' ')})`,
          type: 'danger',
          day: state.gameDay,
          read: false,
        },
      ].slice(-50);
      continue; // drop slot — item is destroyed
    }

    newSlots.push({
      ...slot,
      curingDay:      newCuringDay,
      terpeneProfile: newTerpene,
      visualAppeal:   newVisual,
      qualityScore:   recalcQuality({ ...slot, terpeneProfile: newTerpene, visualAppeal: newVisual }),
      peakDay:        newPeakDay,
      moldRisk:       newMoldRisk,
      status:         newStatus,
    });
  }

  return {
    ...state,
    rooms: state.rooms.map(r => r.id === curingRoom.id ? { ...updatedRoom, curingSlots: newSlots } : r),
    notifications: newNotifications,
  };
}
